(() => {
  const moneyFormat = window.hejMoneyFormat || '{{amount}} kr';

  function formatMoney(cents) {
    const value = Number(cents) / 100;
    const withSeparators = (decimals, thousands, decimal) => {
      const [whole, fraction] = value.toFixed(decimals).split('.');
      const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return fraction ? `${grouped}${decimal}${fraction}` : grouped;
    };
    return moneyFormat.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
      switch (key) {
        case 'amount_no_decimals':
          return withSeparators(0, ',', '.');
        case 'amount_with_comma_separator':
          return withSeparators(2, '.', ',');
        case 'amount_no_decimals_with_comma_separator':
          return withSeparators(0, '.', ',');
        case 'amount_with_space_separator':
          return withSeparators(2, ' ', ',');
        case 'amount_no_decimals_with_space_separator':
          return withSeparators(0, ' ', ',');
        case 'amount_with_apostrophe_separator':
          return withSeparators(2, "'", '.');
        default:
          return withSeparators(2, ',', '.');
      }
    });
  }

  const discounted = (cents, percent) => Math.round(cents - (cents * percent) / 100);

  function onVariantChange(callback) {
    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;
    subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
      if (event?.data?.variant) callback(event.data.variant);
    });
  }

  class HejBundle {
    constructor(root) {
      this.root = root;
      this.price = Number(root.dataset.price);
      this.qtyInput = root.querySelector('[data-hej-bundle-qty]');
      this.template = root.dataset.eachTemplate || '';
      root.addEventListener('change', (event) => {
        if (event.target.matches('input[type="radio"]')) this.select(event.target);
      });
      onVariantChange((variant) => {
        this.price = Number(variant.price);
        this.render();
      });
    }

    select(radio) {
      this.root.querySelectorAll('.hej-bundle__option').forEach((option) => {
        option.classList.toggle('is-on', option.contains(radio));
      });
      this.qtyInput.value = radio.value;
    }

    render() {
      this.root.querySelectorAll('.hej-bundle__option').forEach((option) => {
        const radio = option.querySelector('input');
        const qty = Number(radio.value);
        const percent = Number(radio.dataset.percent);
        const full = this.price * qty;
        const total = discounted(full, percent);
        option.querySelector('[data-hej-total]').textContent = formatMoney(total);
        const fullEl = option.querySelector('[data-hej-full]');
        if (fullEl) fullEl.textContent = formatMoney(full);
        const each = option.querySelector('[data-hej-each]');
        if (each && percent > 0) {
          each.textContent = this.template.replace('%P', percent).replace('%A', formatMoney(Math.round(total / qty)));
        }
      });
    }
  }

  class HejTogether {
    constructor(root) {
      this.root = root;
      this.list = root.querySelector('.hej-together__items');
      this.current = root.querySelector('.is-current');
      this.tiers = { 2: Number(root.dataset.tier2), 3: Number(root.dataset.tier3) };
      this.button = root.querySelector('[data-hej-together-add]');
      this.error = root.querySelector('[data-hej-together-error]');
      this.list.addEventListener('change', () => this.update());
      this.button.addEventListener('click', () => this.add());
      onVariantChange((variant) => {
        this.current.dataset.variantId = variant.id;
        this.current.dataset.price = variant.price;
        this.current.querySelector('[data-hej-current-price]').textContent = formatMoney(variant.price);
        this.update();
      });
      this.load();
    }

    async fetchProducts(intent) {
      const url = `${this.root.dataset.url}.json?product_id=${this.root.dataset.productId}&limit=4&intent=${intent}`;
      const response = await fetch(url);
      if (!response.ok) return [];
      const data = await response.json();
      return data.products || [];
    }

    async load() {
      try {
        let products = await this.fetchProducts('complementary');
        if (products.length < 2) products = products.concat(await this.fetchProducts('related'));
        const seen = new Set([String(this.root.dataset.productId)]);
        const picks = [];
        for (const product of products) {
          const variant = (product.variants || []).find((v) => v.available);
          if (!variant || seen.has(String(product.id))) continue;
          seen.add(String(product.id));
          picks.push({ product, variant });
          if (picks.length === 2) break;
        }
        if (!picks.length) return;
        picks.forEach(({ product, variant }) => this.list.append(this.item(product, variant)));
        this.root.hidden = false;
        this.update();
      } catch (error) {
        console.error(error);
      }
    }

    item(product, variant) {
      const li = document.createElement('li');
      li.className = 'hej-together__item';
      li.dataset.variantId = variant.id;
      li.dataset.price = variant.price;
      const label = document.createElement('label');
      label.className = 'hej-together__label';
      const input = document.createElement('input');
      input.type = 'checkbox';
      input.checked = true;
      const check = document.createElement('span');
      check.className = 'hej-together__check';
      check.setAttribute('aria-hidden', 'true');
      label.append(input, check);
      const image = product.featured_image || product.images?.[0];
      if (image) {
        const img = document.createElement('img');
        const src = typeof image === 'string' ? image : image.src;
        img.src = `${src}${src.includes('?') ? '&' : '?'}width=160`;
        img.alt = '';
        img.width = 64;
        img.height = 64;
        img.loading = 'lazy';
        img.className = 'hej-together__img';
        label.append(img);
      }
      const name = document.createElement('a');
      name.className = 'hej-together__name';
      name.href = product.url;
      const vendor = document.createElement('small');
      vendor.textContent = product.vendor || '';
      name.append(vendor, document.createTextNode(product.title));
      const price = document.createElement('b');
      price.className = 'hej-together__price';
      price.textContent = formatMoney(variant.price);
      li.append(label, name, price);
      return li;
    }

    chosen() {
      return [...this.list.children].filter((li) => li === this.current || li.querySelector('input:checked'));
    }

    update() {
      const items = this.chosen();
      const full = items.reduce((sum, li) => sum + Number(li.dataset.price), 0);
      const percent = this.tiers[Math.min(items.length, 3)] || 0;
      const total = discounted(full, percent);
      this.root.querySelector('[data-hej-together-total]').textContent = formatMoney(total);
      this.root.querySelector('[data-hej-together-full]').textContent = percent ? formatMoney(full) : '';
      const tag = this.root.querySelector('[data-hej-together-tag]');
      tag.textContent = percent
        ? this.root.dataset.saveTemplate.replace('%P', percent).replace('%A', formatMoney(full - total))
        : '';
      tag.hidden = !percent;
    }

    async add() {
      this.button.setAttribute('aria-busy', 'true');
      this.button.disabled = true;
      this.error.hidden = true;
      const cartDrawer = document.querySelector('cart-drawer');
      const body = {
        items: this.chosen().map((li) => ({ id: Number(li.dataset.variantId), quantity: 1 })),
      };
      if (cartDrawer) {
        body.sections = cartDrawer.getSectionsToRender().map((section) => section.id);
        body.sections_url = window.location.pathname;
      }
      try {
        const response = await fetch(`${window.routes.cart_add_url}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(body),
        });
        const data = await response.json();
        if (!response.ok || data.status) throw new Error(data.description || data.message);
        if (cartDrawer && data.sections) {
          cartDrawer.classList.remove('is-empty');
          cartDrawer.renderContents({ ...data, id: body.items[0].id });
          if (typeof publish === 'function') publish(PUB_SUB_EVENTS.cartUpdate, { source: 'hej-together' });
        } else {
          window.location.href = this.root.dataset.cartUrl;
        }
      } catch (error) {
        this.error.textContent = error.message || this.root.dataset.error;
        this.error.hidden = false;
      } finally {
        this.button.disabled = false;
        this.button.removeAttribute('aria-busy');
      }
    }
  }

  function init(scope = document) {
    scope.querySelectorAll('[data-hej-bundle]:not([data-ready])').forEach((root) => {
      root.dataset.ready = '';
      new HejBundle(root);
    });
    scope.querySelectorAll('[data-hej-together]:not([data-ready])').forEach((root) => {
      root.dataset.ready = '';
      new HejTogether(root);
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
